import { h, defineComponent } from "@vue/runtime-core";
import parse from "html-dom-parser";
import { provide, inject, ref, computed, watch } from "vue";
import MjColumn from "mjml-column-snyk";
import MjButton from "mjml-button-snyk";
import MjGroup from "mjml-group-snyk";
import MjBody from "mjml-body-snyk";
import camelToKebab from "./camelToKebabCase.js";
import { mjmlComponentAttributesToVuePropsDefinitions } from "./mjmlComponentAttributesToVuePropsDefinitions.js";
import { getVueRendered } from "./getVueRendered.js";
import { toMediaQueryStyleTags } from "./toMediaQueryStyleTags.js";
import { enhanceMjmlButton } from "./enhanceMjmlButton.js";
import { isComponent } from "./isComponent.js";
import { useHead } from "#imports";
export default function setupMjmlComponent(mjmlComponent, hasColumns) {
  const params = mjmlComponentAttributesToVuePropsDefinitions(
    mjmlComponent.allowedAttributes ? mjmlComponent.allowedAttributes : {},
    mjmlComponent.defaultAttributes ? mjmlComponent.defaultAttributes : {}
  );
  const additionalParams = {
    "data-v-inspector": {
      type: String,
      required: false,
      default: () => void 0
    }
  };
  const vueComponentProps = isComponent(mjmlComponent, MjColumn) ? {
    ...params,
    ...additionalParams,
    numberOfColumns: {
      type: Number,
      required: false,
      default: () => 1,
      validator: (value) => {
        return value > 0;
      }
    },
    mobileWidth: {
      type: String,
      required: false,
      default: () => void 0
    }
  } : {
    ...params,
    ...additionalParams
  };
  return defineComponent({
    name: mjmlComponent.componentName,
    props: vueComponentProps,
    setup(props, { slots }) {
      const parentChildRenderer = inject("mjmlChildRenderFunction", null);
      const numberOfSiblings = inject("numberOfSiblings", 1);
      const mjmlContext = inject("mjmlContext", {
        globalData: {},
        processing: () => ""
      });
      const forceOWADesktop = inject("forceOWADesktop", false);
      const childInstances = ref([]);
      const childRenderFunction = ref(null);
      const currentChildIndex = ref(void 0);
      const mjmlComponentArgs = computed(() => {
        const mjmlProps = {
          // MJML itself sets this up with the same number in core already, so here we are.
          index: currentChildIndex.value,
          first: currentChildIndex.value === 0,
          last: (currentChildIndex.value || 0) - 1 < numberOfSiblings,
          sibling: numberOfSiblings,
          nonRawSiblings: numberOfSiblings
        };
        const args = {
          context: mjmlContext,
          children: childInstances.value,
          attributes: Object.fromEntries(
            Object.entries(props).filter(([_, v]) => v !== void 0).map(([k, v]) => [
              k === "mobileWidth" ? k : camelToKebab(k),
              v
            ])
          ),
          props: mjmlProps,
          content: void 0
        };
        if (mjmlComponent.endingTag) {
          args.content = "<!--[SLOT CONTENT]-->";
        }
        return args;
      });
      const mjmlComponentInstance = ref(new mjmlComponent(mjmlComponentArgs.value));
      watch(mjmlComponentArgs, (newValue) => {
        mjmlComponentInstance.value = new mjmlComponent(newValue);
      });
      watch(mjmlComponentInstance, (newValue) => {
        if (!mjmlComponent.endingTag) {
          newValue.renderChildren = (_, options) => {
            childRenderFunction.value = options.renderer;
            return "<!--[SLOT CONTENT]-->";
          };
        }
      });
      if (!mjmlComponent.endingTag) {
        mjmlComponentInstance.value.renderChildren = (_, options) => {
          childRenderFunction.value = options && options.renderer ? options.renderer : (component) => component.render();
          return "<!--[SLOT CONTENT]-->";
        };
      }
      const mjmlDom = computed(() => {
        let rendered = parentChildRenderer && parentChildRenderer.value ? parentChildRenderer.value(mjmlComponentInstance.value) : mjmlComponentInstance.value.render();
        if (isComponent(mjmlComponent, MjButton) && "borderRadius" in props && props.borderRadius !== "0") {
          rendered = enhanceMjmlButton(rendered, mjmlComponentInstance, props.borderRadius, "href" in props ? props.href : "");
        }
        return parse(rendered);
      });
      const mjmlMediaQueryHeadStyles = computed(() => {
        if (!isComponent(mjmlComponent, MjGroup) && !isComponent(mjmlComponent, MjColumn)) {
          return [];
        }
        const className = mjmlComponentInstance.value.getColumnClass();
        const columnClassParts = className.replace("mj-column-", "").split("-");
        const unit = columnClassParts[0] === "per" ? "%" : "px";
        const parsedWidth = columnClassParts[1];
        return toMediaQueryStyleTags(parsedWidth, unit, className, mjmlContext.globalData.breakpoint, forceOWADesktop);
      });
      const bodyBackgroundColor = computed(() => {
        if (isComponent(mjmlComponent, MjBody)) {
          return null;
        }
        return mjmlComponentInstance.value.getAttribute("background-color");
      });
      const headSettings = computed(() => {
        const headSettings2 = {};
        if (isComponent(mjmlComponent, MjBody) && bodyBackgroundColor.value) {
          headSettings2.bodyAttrs = {
            style: `word-spacing:normal;background-color:${bodyBackgroundColor.value};`
          };
        }
        if (isComponent(mjmlComponent, MjGroup) || isComponent(mjmlComponent, MjColumn)) {
          headSettings2.style = mjmlMediaQueryHeadStyles.value;
        }
        return headSettings2;
      });
      useHead(headSettings.value);
      const numberOfColumns = computed(() => {
        return hasColumns && "numberOfColumns" in props ? props.numberOfColumns : 1;
      });
      provide("mjmlChildRenderFunction", childRenderFunction);
      provide("numberOfSiblings", numberOfColumns);
      return () => getVueRendered(h, mjmlDom.value, slots.default, mjmlComponent.componentName, {
        "data-v-inspector": "data-v-inspector" in props ? props["data-v-inspector"] : ""
      });
    }
  });
}
